import {
  APPLICATION_STATUS_TO_SHOW_CONTACT_DETAILS,
  ASTERIKS_FOR_NAME,
} from '@application/constants';
import { useRouter } from '@application/utils';
import type { AccountGetBuyerApplicationsQuery } from '@gql/graphql';
import {
  COLOR_PER_DOG_BREEDER_APPLICATION_STATUS,
  PLACEHOLDER_IMAGE_USER,
} from '@presentation/constants';
import type { MtApplicationsPageData } from '@presentation/pages';
import type { CardDataPerson } from '@presentation/pages/MtApplications/components/Card/types';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';

type Application = NonNullable<
  AccountGetBuyerApplicationsQuery['accountMe']
>['applications'][number];

export interface UseFormatApplicationsParams {
  applications: Application[];
  onActionsClick?: (
    application: Application,
  ) => MtApplicationsPageData['cards'][0]['onActionsClick'];
  hasRemainingDaysForFreeApplication: boolean;
}

export const useFormatApplications = (params: UseFormatApplicationsParams) => {
  const { routeLocationDescriptor, translate } = useRouter();
  const { onActionsClick, applications, hasRemainingDaysForFreeApplication } = params;

  const shouldHideDetails = useCallback(
    (application: Application): boolean => {
      if (hasRemainingDaysForFreeApplication) {
        return false;
      }
      return !APPLICATION_STATUS_TO_SHOW_CONTACT_DETAILS.includes(application.status);
    },
    [hasRemainingDaysForFreeApplication],
  );

  const formatApplicationsCard = useCallback(
    (application: Application): MtApplicationsPageData['cards'][0] => {
      const getPerson = (): CardDataPerson => {
        const image = application.dogBreeder.profileImg ?? PLACEHOLDER_IMAGE_USER;

        if (shouldHideDetails(application)) {
          return {
            title: ASTERIKS_FOR_NAME,
            image,
            type: 'buyer',
          };
        }

        return {
          title: `${application.dogBreeder.account.firstName} ${application.dogBreeder.account.lastName}`,
          image,
          type: 'buyer',
        };
      };

      return {
        link: routeLocationDescriptor('mtApplications', { params: [application.id.toString()] }),
        person: getPerson(),
        onActionsClick: onActionsClick?.(application),
        date: dayjs(application.puppy?.dateOfBirth ?? application.createdAt),
        status: {
          content: translate(`enum.dogBreederApplicationStatus.${application.status}`),
          theme: COLOR_PER_DOG_BREEDER_APPLICATION_STATUS[application.status],
        },
        breed: {
          title: application.breedPreference.translations[0].name ?? '',
        },
      };
    },
    [onActionsClick, routeLocationDescriptor, shouldHideDetails, translate],
  );

  const formattedApplicationsCards = useMemo((): MtApplicationsPageData['cards'] => {
    return applications.map((application) => formatApplicationsCard(application));
  }, [applications, formatApplicationsCard]);

  return { formattedApplicationsCards };
};
